import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ThemeToggle } from '../components/ThemeToggle'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

export function Imprint() {
  const { lang } = useParams<{ lang: string }>()
  const { t } = useTranslation('legal')

  useEffect(() => {
    if (lang) {
      localStorage.setItem('i18nextLng', lang)
    }
  }, [lang])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const currentLang = lang || 'en'

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted to-background dark:from-background dark:to-muted py-12 px-4 sm:px-6 lg:px-8">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6 transition-colors duration-200"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t('imprint.backToHome')}
            </Link>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              {t('imprint.title')}
            </h1>
            <p className="mt-4 text-muted-foreground">
              {t('imprint.subtitle')}
            </p>
          </div>

          {/* Content Card */}
          <motion.div
            className="bg-card rounded-2xl shadow-xl p-8 space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Company Information */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('imprint.companyInfo.title')}
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.companyName')}
                  </span>{' '}
                  {t('imprint.companyInfo.companyNameValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.legalForm')}
                  </span>{' '}
                  {t('imprint.companyInfo.legalFormValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.registeredOffice')}
                  </span>{' '}
                  {t('imprint.companyInfo.registeredOfficeValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.registrationCourt')}
                  </span>{' '}
                  {t('imprint.companyInfo.registrationCourtValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.registrationNumber')}
                  </span>{' '}
                  {t('imprint.companyInfo.registrationNumberValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.companyInfo.vatId')}
                  </span>{' '}
                  {t('imprint.companyInfo.vatIdValue')}
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('imprint.contactInfo.title')}
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.contactInfo.address')}
                  </span>{' '}
                  <br />
                  {t('imprint.contactInfo.addressValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.contactInfo.email')}
                  </span>{' '}
                  <a
                    href="mailto:info@securesight.ai"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    info@securesight.ai
                  </a>
                </p>
                <p>
                  <span className="font-medium text-foreground">
                    {t('imprint.contactInfo.phone')}
                  </span>{' '}
                  <a
                    href="tel:+4915563347909"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    +49 15563 347909
                  </a>
                </p>
              </div>
            </section>

            {/* Managing Directors */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('imprint.managingDirectors.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('imprint.managingDirectors.names')}
              </p>
            </section>

            {/* Responsible for Content */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('imprint.responsibleForContent.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('imprint.responsibleForContent.name')}
                <br />
                {t('imprint.responsibleForContent.address')}
              </p>
            </section>

            {/* Disclaimer */}
            <section className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-l-4 border-blue-500">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('imprint.disclaimer.title')}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t('imprint.disclaimer.text')}
              </p>
            </section>

            {/* EU Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('imprint.disputeResolution.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('imprint.disputeResolution.text')}{' '}
                <a
                  href={t('imprint.disputeResolution.url')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {t('imprint.disputeResolution.linkText')}
                </a>
                . {t('imprint.disputeResolution.notObliged')}
              </p>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
